import { Logger } from "../utils/Logger.js";
import { FragmentCollector } from "../services/FragmentCollector.js";
import { CoordinateConverter } from "../services/CoordinateConverter.js";

/**
 * Base class for positioning strategies
 */
export class BasePositioningStrategy {
	constructor(viewer) {
		this.viewer = viewer;
		this.logger = Logger.createLogger("PositioningStrategy");
		this.fragmentCollector = new FragmentCollector(viewer);
		this.coordinateConverter = new CoordinateConverter(viewer);
	}

	/**
	 * Create positioning result object
	 * @param {Object} position - Screen position
	 * @param {boolean} isVisible - Whether marker should be visible
	 * @param {boolean} isOnScreen - Whether position is on screen
	 * @returns {Object} Positioning result
	 */
	createResult(position, isVisible = true, isOnScreen = true) {
		return {
			position,
			isVisible,
			isOnScreen,
			strategy: this.getStrategyName(),
		};
	}

	/**
	 * Calculate position for a 2D point marker (no dbId)
	 * @param {Object} modelPoint - Model coordinates
	 * @param {Object} model - Model reference
	 * @param {HTMLElement} element - Marker element
	 * @returns {Promise<Object>} Positioning result
	 */
	async calculatePositionFor2DPoint(modelPoint) {
		this.logger.debug(
			`Calculating 2D point position for model coordinates:`,
			modelPoint
		);

		try {
			// Convert model coordinates to client coordinates for proper positioning
			let clientPos = null;

			if (
				this.coordinateConverter &&
				typeof this.coordinateConverter.modelToClient === "function"
			) {
				clientPos = this.coordinateConverter.modelToClient(modelPoint);
			}

			// Fallback: try using worldToClient directly
			if (!clientPos && this.viewer) {
				try {
					clientPos = this.viewer.worldToClient(modelPoint);
				} catch (error) {
					this.logger.warn("worldToClient failed:", error);
				}
			}

			// Final fallback: if coordinates look like they're already in screen space, use them
			if (!clientPos && modelPoint) {
				const viewportSize = this.coordinateConverter.getViewportSize();
				if (
					modelPoint.x >= 0 &&
					modelPoint.x <= viewportSize.width &&
					modelPoint.y >= 0 &&
					modelPoint.y <= viewportSize.height
				) {
					clientPos = {
						x: modelPoint.x,
						y: modelPoint.y,
					};
					this.logger.debug(
						"Using model point as screen coordinates (fallback)"
					);
				}
			}

			// Last resort: center of viewport
			if (!clientPos) {
				clientPos = this.coordinateConverter.getViewportCenter();
				this.logger.warn(
					"Using viewport center as fallback for 2D point marker"
				);
			}

			const isOnScreen = this.coordinateConverter.isWithinViewport(
				clientPos,
				50
			);
			return this.createResult(clientPos, true, isOnScreen);
		} catch (error) {
			this.logger.error("Error calculating 2D point position:", error);
			const center = this.coordinateConverter.getViewportCenter();
			return this.createResult(center, true, true);
		}
	}

	/**
	 * Update position for a 2D point marker
	 * @param {Object} marker - Marker data with modelPoint and model
	 * @returns {Promise<Object>} Positioning result
	 */
	async updatePositionFor2DPoint(marker) {
		if (!marker.modelPoint) {
			throw new Error("Marker missing modelPoint for 2D positioning");
		}
		return this.calculatePositionFor2DPoint(marker.modelPoint);
	}
}

/**
 * 3D positioning strategy
 */
export class ThreeDPositioningStrategy extends BasePositioningStrategy {
	getStrategyName() {
		return "3D";
	}

	isCompatible() {
		return this.viewer?.model && !this.viewer.model.is2d();
	}

	async calculatePosition(dbId, element) {
		try {
			// Get unified bounding box
			const bbox = this.fragmentCollector.getUnifiedBoundingBox(dbId);
			if (!bbox) {
				this.logger.warn(`No bounding box found for dbId ${dbId}`);
				return this.getFallbackPosition();
			}

			// Calculate center point at top of object
			const center = bbox.getCenter();
			center.y = bbox.max.y; // Position at top

			// Convert to screen coordinates
			const screenPos = this.coordinateConverter.worldToScreen(center);
			if (!screenPos) {
				this.logger.warn(`Failed to convert world to screen for dbId ${dbId}`);
				return this.getFallbackPosition();
			}

			const isOnScreen = this.coordinateConverter.isWithinViewport(
				screenPos,
				50
			);
			const isVisible = true; // Could check node visibility here

			return this.createResult(screenPos, isVisible, isOnScreen);
		} catch (error) {
			this.logger.error(
				`Error calculating 3D position for dbId ${dbId}`,
				error
			);
			return this.getFallbackPosition();
		}
	}

	async updatePosition(marker) {
		return this.calculatePosition(marker.dbId, marker.element);
	}

	getFallbackPosition() {
		const center = this.coordinateConverter.getViewportCenter();
		return this.createResult(center, true, true);
	}
}

/**
 * 2D positioning strategy
 */
export class TwoDPositioningStrategy extends BasePositioningStrategy {
	getStrategyName() {
		return "2D";
	}

	isCompatible() {
		return this.viewer?.model && this.viewer.model.is2d();
	}

	async calculatePosition(dbId, element) {
		this.logger.debug(`Calculating 2D position for dbId ${dbId}`);

		try {
			// For 2D views, try to get fragment bounds directly
			const fragmentIds = await this.fragmentCollector.collectFragmentIds(dbId);
			if (fragmentIds.length === 0) {
				this.logger.warn(`No fragments found for dbId ${dbId}`);
				return this.getFallbackPosition(dbId);
			}

			// Try to get 2D bounds from first valid fragment
			for (const fragId of fragmentIds) {
				const bounds2d = this.fragmentCollector.get2DFragmentBounds(fragId);
				if (bounds2d) {
					const center = bounds2d.getCenter();
					// Position above the object
					const screenPos = {
						x: center.x,
						y: center.y - (bounds2d.max.y - bounds2d.min.y) / 2,
					};

					// Convert model coordinates to client coordinates
					const clientPos = this.coordinateConverter.modelToClient(screenPos);
					const finalPos = clientPos || screenPos;

					const isOnScreen = this.coordinateConverter.isWithinViewport(
						finalPos,
						50
					);
					return this.createResult(finalPos, true, isOnScreen);
				}
			}

			// If no 2D bounds found, try fallback approach
			return this.getFallbackPosition(dbId);
		} catch (error) {
			this.logger.error(
				`Error calculating 2D position for dbId ${dbId}`,
				error
			);
			return this.getFallbackPosition(dbId);
		}
	}

	async updatePosition(marker) {
		return this.calculatePosition(marker.dbId, marker.element);
	}

	async getFallbackPosition(dbId) {
		this.logger.debug(`Using 2D fallback positioning for dbId ${dbId}`);

		try {
			// Try selection-based approach
			return new Promise((resolve) => {
				this.viewer.select([dbId]);

				setTimeout(() => {
					const bounds = this.viewer.getSelectionBounds();
					if (bounds && !bounds.isEmpty()) {
						const center = bounds.center();
						const screenPos = {
							x: center.x,
							y: bounds.min.y - 20, // Position above
						};

						const clientPos = this.coordinateConverter.modelToClient(screenPos);
						const finalPos = clientPos || screenPos;
						const isOnScreen = this.coordinateConverter.isWithinViewport(
							finalPos,
							50
						);

						this.viewer.clearSelection();
						resolve(this.createResult(finalPos, true, isOnScreen));
					} else {
						// Last resort: center of viewport
						const center = this.coordinateConverter.getViewportCenter();
						resolve(this.createResult(center, true, true));
					}
				}, 100);
			});
		} catch (error) {
			this.logger.error(
				`Error in 2D fallback positioning for dbId ${dbId}`,
				error
			);
			const center = this.coordinateConverter.getViewportCenter();
			return this.createResult(center, true, true);
		}
	}
}

/**
 * Factory for creating positioning strategies
 */
export class PositioningStrategyFactory {
	static createStrategy(viewer) {
		const logger = Logger.createLogger("PositioningStrategyFactory");

		if (!viewer || !viewer.model) {
			logger.warn("No viewer or model available, using 3D strategy as default");
			return new ThreeDPositioningStrategy(viewer);
		}

		const is2D = viewer.model.is2d();
		const strategy = is2D
			? new TwoDPositioningStrategy(viewer)
			: new ThreeDPositioningStrategy(viewer);

		return strategy;
	}
}
